import { getUserId } from '@anu/utilities/settings';
import * as fields from '@anu/utilities/fields';

/**
 * Reads all progress stored in local storage and optionally removes expired data.
 *
 * @param {int} expireAfter
 *   If greater than 0, then course progress older than expireAfter milliseconds
 *   will be deleted from sessionStorage.
 */
const getSessionStorageProgress = (expireAfter = 0) => {
  const items = {};

  try {
    const keys = Object.keys(window.sessionStorage).filter((key) => key.startsWith('Anu.progress.'));
    let i = keys.length;
    while (i--) {
      const item = window.sessionStorage.getItem(keys[i]);
      if (!item || (expireAfter > 0 && item.updated < Date.now() - expireAfter)) {
        window.sessionStorage.removeItem(keys[i]);
      } else {
        items[keys[i]] = item;
      }
    }
  } catch (error) {
    console.error(error);
  }

  return items;
};

/**
 * Returns sessionStorage key that depends on user id and course id.
 */
const courseProgressKey = (courseId) => `Anu.progress.u${getUserId()}.course${courseId}`;

/**
 * Bootstraps progress data based on server data and sessionStorage data.
 */
const prepareCourseProgress = (node) => {
  const courseId = fields.getNumberValue(node, 'nid');
  const key = courseProgressKey(courseId);

  // Gather any progress newer than 1 month from localStorage.
  const items = getSessionStorageProgress(30 * 24 * 60 * 60 * 1000);
  const storedResult = items && items[key]
    ? JSON.parse(items[key])
    : {};

  // Collect lessons in sequence.
  const lessons = [];
  fields.getArrayValue(node, 'field_course_module').forEach(
    module =>  fields.getArrayValue(module, 'field_module_lessons').forEach(
      lesson => lessons.push({
        id: lesson.target_id ? lesson.target_id : fields.getNumberValue(lesson, 'nid'),
        url: lesson.url ? lesson.url : fields.getNodeUrl(lesson),
      })
    )
  );

  // Create progress.
  const result = {};
  lessons.forEach((lesson, i) => {
    result[lesson.id] = storedResult && storedResult[lesson.id]
      ? storedResult[lesson.id]
      : {
        prev: lessons[i - 1] ? String(lessons[i - 1].id) : 0,
        next: lessons[i + 1] ? String(lessons[i + 1].id) : 0,
        completed: 0,
        restricted: 0,
        url: lesson.url,
      };
  })

  return result;
};

/**
 * Returns lesson's isCompleted property based on progress data.
 */
const isLessonCompleted = (course, lessonId) => {
  if (
    course &&
    course.track_progress &&
    course.progress &&
    course.progress[lessonId] &&
    course.progress[lessonId].completed
  ) {
    return true;
  }

  return false;
};

/**
 * Returns lesson's isRestricted property based on progress data.
 */
const isLessonRestricted = (course, lessonId) => {
  if (
    course &&
    course.linear_progress &&
    course.progress &&
    course.progress[lessonId] &&
    course.progress[lessonId].restricted
  ) {
    return true;
  }

  return false;
};

/**
 * Makes API request to mark given lesson ids as completed.
 */
const postCompletedLessons = (lessonIds = []) => {
};

/**
 * Lesson "complete" callback.
 *
 * Marks a lesson as completed in sessionStorage and attempts to push all pending
 * completed lessons to the server.
 */
const completeLesson = (course, lessonId) => {
  if (!course || !course.progress || !course.progress[lessonId]) {
    // Progress is not enabled. Nothing to do.
    return;
  }
  // Mark current lesson as completed.
  const updatedProgress = {
    ...course.progress,
    [lessonId]: {
      ...course.progress[lessonId],
      completed: 1,
      needsSync: 1,
    },
    updated: Date.now(),
  };

  // Mark next lesson as not restricted.
  if (course.progress[lessonId].next && updatedProgress[course.progress[lessonId].next]) {
    updatedProgress[course.progress[lessonId].next].restricted = 0;
  }
  try {
    window.sessionStorage.setItem(courseProgressKey(course.id), JSON.stringify(updatedProgress));
  } catch (error) {
    console.error(error);
  }

  const lessonIds = Object.keys(updatedProgress).filter(
    (lessonId) => updatedProgress[lessonId] && updatedProgress[lessonId].needsSync
  );
  postCompletedLessons(lessonIds);
};

/**
 * Returns progress percent for the given course progress.
 */
const calculateProgressPercent = (progress) => {
  if (!progress) {
    return 0;
  }

  const lessons = Object.values(progress);
  const totalLessons = lessons.length;
  const completedLessons = lessons.filter((item) => item.completed).length;

  if (totalLessons > 0) {
    return Math.round((completedLessons * 100) / totalLessons);
  }

  return 0;
};

/**
 * Pushes all pending completed lessons across all courses to the server.
 * Should be triggered on network "online" event.
 */
const syncAll = async () => {
};

export {
  calculateProgressPercent,
  completeLesson,
  courseProgressKey,
  isLessonCompleted,
  isLessonRestricted,
  prepareCourseProgress,
  syncAll,
};
